import { useEffect } from "react";
import { Link } from "react-router";
import { useLanguage } from "../../i18n/LanguageContext";
import { translations } from "../../i18n/translations";
import contentPl from "../../../public/docs/assistly-privacy-policy-pl.md?raw";
import contentEn from "../../../public/docs/assistly-privacy-policy-en.md?raw";

// ── Simple markdown renderer ──────────────────────────────────────────────────

function renderInline(text: string): React.ReactNode {
  const parts = text.split(/(\*\*.*?\*\*)/g);
  return parts.map((part, i) =>
    part.startsWith("**") && part.endsWith("**") ? (
      <strong key={i}>{part.slice(2, -2)}</strong>
    ) : (
      part
    )
  );
}

function MarkdownRenderer({ content }: { content: string }) {
  const lines = content.split("\n");
  const nodes: React.ReactNode[] = [];
  let i = 0;
  let key = 0;

  while (i < lines.length) {
    const line = lines[i];

    // Fenced code block (used for version/footer in these docs)
    if (line.trim() === "```") {
      const blockLines: string[] = [];
      i++;
      while (i < lines.length && lines[i].trim() !== "```") {
        if (lines[i].trim()) blockLines.push(lines[i].trim());
        i++;
      }
      i++; // skip closing ```
      if (blockLines.length > 0) {
        nodes.push(
          <p
            key={key++}
            className="text-xs text-center mt-2"
            style={{ color: "var(--foreground-faint)", fontFamily: "var(--font-body)" }}
          >
            {blockLines.map((l, j) => (
              <span key={j}>{l}{j < blockLines.length - 1 && <br />}</span>
            ))}
          </p>
        );
      }
      continue;
    }

    // H1
    if (line.startsWith("# ")) {
      nodes.push(
        <h1
          key={key++}
          className="text-2xl md:text-3xl text-center mt-12 mb-2"
          style={{ color: "var(--gold)", fontFamily: "var(--font-display)", letterSpacing: "0.05em" }}
        >
          {renderInline(line.slice(2))}
        </h1>
      );
      i++;
      continue;
    }

    // H2
    if (line.startsWith("## ")) {
      const text = line.slice(3);
      // "Assistly.tools" subtitle under the main title
      if (text === "Assistly.tools") {
        nodes.push(
          <p
            key={key++}
            className="text-center mb-2"
            style={{ color: "var(--gold)", fontFamily: "var(--font-body)", fontSize: "0.9rem" }}
          >
            {text}
          </p>
        );
      } else {
        nodes.push(
          <h2
            key={key++}
            className="text-base font-semibold mt-8 mb-2 pt-4"
            style={{ color: "var(--foreground)", fontFamily: "var(--font-display)", borderTop: "1px solid var(--border)" }}
          >
            {renderInline(text)}
          </h2>
        );
      }
      i++;
      continue;
    }

    // H3
    if (line.startsWith("### ")) {
      nodes.push(
        <h3
          key={key++}
          className="text-sm font-semibold mt-5 mb-2"
          style={{ color: "var(--gold)", fontFamily: "var(--font-body)" }}
        >
          {renderInline(line.slice(4))}
        </h3>
      );
      i++;
      continue;
    }

    // List
    if (line.startsWith("- ")) {
      const items: string[] = [];
      while (i < lines.length && (lines[i].startsWith("- ") || lines[i].startsWith("  "))) {
        if (lines[i].startsWith("- ")) {
          items.push(lines[i].slice(2).trim());
        } else {
          const cont = lines[i].trim();
          if (cont && items.length > 0) {
            items[items.length - 1] += " " + cont;
          }
        }
        i++;
      }
      nodes.push(
        <ul key={key++} className="list-disc pl-5 my-2 space-y-1">
          {items.map((item, j) => (
            <li
              key={j}
              className="text-sm"
              style={{ color: "var(--foreground-muted)", fontFamily: "var(--font-body)", lineHeight: "1.7" }}
            >
              {renderInline(item)}
            </li>
          ))}
        </ul>
      );
      continue;
    }

    // Empty line
    if (line.trim() === "") {
      i++;
      continue;
    }

    // Paragraph — collect consecutive non-special lines
    const paragraphLines: string[] = [];
    while (
      i < lines.length &&
      lines[i].trim() !== "" &&
      !lines[i].startsWith("#") &&
      !lines[i].startsWith("- ") &&
      lines[i].trim() !== "```"
    ) {
      paragraphLines.push(lines[i]);
      i++;
    }
    if (paragraphLines.length > 0) {
      nodes.push(
        <p
          key={key++}
          className="text-sm my-2"
          style={{ color: "var(--foreground-muted)", fontFamily: "var(--font-body)", lineHeight: "1.75" }}
        >
          {renderInline(paragraphLines.join(" "))}
        </p>
      );
    }
  }

  return <>{nodes}</>;
}

// ── Page ──────────────────────────────────────────────────────────────────────

export default function LegalPage() {
  const { language, setLanguage } = useLanguage();
  const t = translations[language];
  const content = language === "pl" ? contentPl : contentEn;

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen" style={{ background: "var(--background)" }}>
      {/* Header */}
      <header
        className="sticky top-0 z-50 px-6 py-4"
        style={{
          background: "rgba(10, 10, 11, 0.88)",
          backdropFilter: "blur(12px)",
          borderBottom: "1px solid var(--border)",
        }}
      >
        <div className="max-w-4xl mx-auto flex items-center justify-between">
          <Link
            to="/"
            className="text-sm transition-colors"
            style={{ color: "var(--foreground-muted)", fontFamily: "var(--font-body)" }}
            onMouseEnter={(e) => (e.currentTarget.style.color = "var(--gold)")}
            onMouseLeave={(e) => (e.currentTarget.style.color = "var(--foreground-muted)")}
          >
            {t.legal.backToHome}
          </Link>

          <span
            className="text-lg tracking-widest uppercase"
            style={{ fontFamily: "var(--font-display)", color: "var(--gold)", letterSpacing: "0.22em", fontStyle: "italic" }}
          >
            Assistly
          </span>

          <div className="flex items-center gap-2" style={{ fontFamily: "var(--font-mono)", fontSize: "0.75rem" }}>
            <button
              onClick={() => setLanguage("pl")}
              style={{
                color: language === "pl" ? "var(--gold)" : "var(--foreground-muted)",
                fontWeight: language === "pl" ? 600 : 400,
                background: "none",
                border: "none",
                cursor: "pointer",
                padding: 0,
              }}
            >
              PL
            </button>
            <span style={{ color: "var(--border-strong)" }}>|</span>
            <button
              onClick={() => setLanguage("en")}
              style={{
                color: language === "en" ? "var(--gold)" : "var(--foreground-muted)",
                fontWeight: language === "en" ? 600 : 400,
                background: "none",
                border: "none",
                cursor: "pointer",
                padding: 0,
              }}
            >
              EN
            </button>
          </div>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-4xl mx-auto px-6 py-12 pb-24">
        <MarkdownRenderer content={content} />
      </main>

      {/* Bottom bar */}
      <footer className="px-6 py-6" style={{ borderTop: "1px solid var(--border)" }}>
        <p className="text-center text-xs" style={{ color: "var(--foreground-faint)", fontFamily: "var(--font-body)" }}>
          {t.footer.copyright}
        </p>
      </footer>
    </div>
  );
}
